import type { Lesson } from './courses';

/**
 * Parcours 5 : Marketing & Campagnes — 5 lecons.
 */
export const MARKETING_AUTOMATION_LESSONS: Lesson[] = [
  {
    slug: 'campaign-basics',
    title: "Anatomie d'une campagne marketing",
    duration: '4 min',
    glossaryTerms: ['campaigns', 'kpi', 'roi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Une campagne marketing est un ensemble coordonne d'actions visant a atteindre un objectif precis sur une periode definie. Contrairement au marketing continu (blog, reseaux sociaux), une campagne a un debut, une fin et des metriques de succes claires.\n\nLes elements d'une campagne :\n- **Objectif** — Ce que vous voulez accomplir (leads, inscriptions, ventes)\n- **Audience** — A qui vous vous adressez (segment, persona)\n- **Message** — Ce que vous communiquez (proposition de valeur)\n- **Canaux** — Ou vous diffusez (email, LinkedIn, blog, ads)\n- **Budget** — Combien vous investissez\n- **Metriques** — Comment vous mesurez le succes",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Les campagnes structurees generent 3x plus de ROI que les actions marketing dispersees (HubSpot). En concentrant vos efforts sur un objectif precis pendant une periode definie, vous maximisez l'impact de chaque euro investi.\n\nSans structure de campagne, vous risquez le \"spray and pray\" : diffuser partout sans savoir ce qui fonctionne.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Definissez l'objectif SMART** — Ex: generer 50 leads qualifies en 4 semaines\n2. **Identifiez votre audience** — Quel segment de votre ICP visez-vous ?\n3. **Creez les contenus** — Emails, landing page, posts sociaux, ads\n4. **Choisissez vos canaux** — Ou votre audience est-elle la plus active ?\n5. **Lancez et suivez** — Mesurez quotidiennement les KPIs (CTR, conversions, cout/lead)\n6. **Optimisez en cours de route** — Ajustez le message, les canaux, le budget",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le module **Campaigns** vous permet de creer et suivre vos campagnes marketing. Definissez les objectifs, ajoutez les contenus associes (generes via la Content Factory), et suivez les metriques de performance en temps reel.",
      },
    ],
    linkedModule: {
      label: 'Campaigns',
      path: '/growth/campaigns',
      icon: 'Zap',
    },
  },
  {
    slug: 'tracking-attribution',
    title: 'Tracking & Attribution',
    duration: '4 min',
    glossaryTerms: ['campaigns', 'roi', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le tracking (suivi) et l'attribution permettent de savoir d'ou viennent vos leads et clients. L'objectif : comprendre quels canaux et quelles actions generent le plus de valeur.\n\nLes outils cles :\n- **UTM** — Parametres ajoutes aux URLs pour tracker la source (utm_source, utm_medium, utm_campaign)\n- **Attribution** — Modele qui attribue le merite de la conversion a un ou plusieurs points de contact\n- **Conversion** — L'action finale que vous mesurez (inscription, achat, demo)",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Sans tracking, vous ne savez pas quel canal genere le meilleur ROI. Vous risquez d'investir dans des canaux peu performants et de sous-investir dans ceux qui fonctionnent.\n\nExemple : si vos emails de cold outreach generent 60% de vos leads mais ne representent que 10% de votre budget marketing, il y a un desequilibre a corriger. Le tracking vous donne cette visibilite.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Ajoutez des UTM a toutes vos URLs** — Source, medium, campaign, content\n2. **Utilisez un modele d'attribution** — Premier clic, dernier clic, ou multi-touch\n3. **Definissez vos conversions** — Quelles actions comptent comme une \"conversion\" ?\n4. **Suivez le cout par lead (CPL)** — Budget du canal / nombre de leads generes\n5. **Calculez le ROI par canal** — Revenu genere / cout du canal",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Chaque campagne dans le module **Campaigns** peut etre associee a des liens tracks avec UTM. Le **Marketing Dashboard** agrege les donnees pour vous montrer quels canaux et quelles campagnes performent le mieux.",
      },
    ],
    linkedModule: {
      label: 'Campaigns',
      path: '/growth/campaigns',
      icon: 'Zap',
    },
  },
  {
    slug: 'campaign-optimization',
    title: 'Optimiser ses campagnes',
    duration: '3 min',
    glossaryTerms: ['campaigns', 'kpi', 'roi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "L'optimisation de campagne consiste a ameliorer les performances en cours de route en ajustant les variables cles. Les metriques a surveiller :\n\n- **CTR** (Click-Through Rate) — Pourcentage de personnes qui cliquent sur votre lien\n- **CPC** (Cost Per Click) — Cout de chaque clic (pour les campagnes payantes)\n- **CPL** (Cost Per Lead) — Cout de chaque lead genere\n- **Taux de conversion** — Pourcentage de visiteurs qui deviennent leads\n- **ROI** — Retour sur investissement global de la campagne",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Une campagne rarement performe a son maximum des le premier jour. Les meilleures equipes marketing testent et optimisent continuellement : sujet d'email, visuel, CTA, audience, heure d'envoi.\n\nUne amelioration de 10% du CTR sur un email envoye a 10 000 personnes peut generer des dizaines de leads supplementaires sans augmenter le budget.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Mesurez les KPIs des le jour 1** — Ne pas attendre la fin pour regarder les chiffres\n2. **Identifiez le maillon faible** — Ou perdez-vous le plus de monde dans le funnel ?\n3. **Testez une variable a la fois** — Sujet d'email, CTA, visuel, audience\n4. **Doublez ce qui marche** — Augmentez le budget sur les canaux performants\n5. **Coupez ce qui ne marche pas** — Pas de sentimentalite, les chiffres parlent",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le module **Campaigns** affiche les metriques de chaque campagne en temps reel. Utilisez ces donnees pour optimiser vos actions en cours. Le **Marketing Dashboard** vous donne une vue d'ensemble cross-campagnes.",
      },
    ],
    linkedModule: {
      label: 'Campaigns',
      path: '/growth/campaigns',
      icon: 'Zap',
    },
  },
  {
    slug: 'multi-channel',
    title: 'Strategie multi-canal',
    duration: '3 min',
    glossaryTerms: ['campaigns', 'content-factory', 'content-calendar'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "La strategie multi-canal consiste a diffuser vos messages marketing sur plusieurs canaux simultanement pour maximiser votre portee et toucher votre audience la ou elle se trouve.\n\nCanaux typiques pour un SaaS B2B :\n- **Email** — Newsletter, nurturing, cold outreach\n- **LinkedIn** — Posts, articles, messages directs\n- **Blog** — Articles SEO, thought leadership\n- **Publicite** — Google Ads, LinkedIn Ads, retargeting\n- **Evenements** — Webinars, conferences, meetups",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Un prospect a besoin en moyenne de 8 a 12 points de contact avant de prendre une decision d'achat (Salesforce). Si vous n'etes present que sur un seul canal, vous risquez de ne jamais atteindre ce seuil.\n\nLes campagnes multi-canal ont un taux d'engagement 300% superieur aux campagnes mono-canal. Mais attention : multi-canal ne signifie pas \"etre partout\". Choisissez 3-4 canaux ou votre audience est reellement presente.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Identifiez les canaux de votre audience** — Ou passent-ils du temps ? Ou cherchent-ils des solutions ?\n2. **Adaptez le message a chaque canal** — Le meme contenu doit etre reformule pour chaque format\n3. **Coordonnez le timing** — Lancez sur tous les canaux dans la meme fenetre\n4. **Creez un calendrier unifie** — Visualisez l'ensemble des publications sur tous les canaux\n5. **Mesurez par canal** — Identifiez quel canal genere le meilleur ROI",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Combinez la **Content Factory** (generation multi-format) avec le **Content Calendar** (planification) et les **Campaigns** (suivi). Generez un article de blog, declinez-le en post LinkedIn et en email, planifiez tout dans le calendrier et tracez les resultats dans la campagne.",
      },
    ],
    linkedModule: {
      label: 'Campaigns',
      path: '/growth/campaigns',
      icon: 'Zap',
    },
  },
  {
    slug: 'your-campaigns',
    title: 'Gerer vos campagnes dans FO',
    duration: '3 min',
    glossaryTerms: ['campaigns', 'marketing-dashboard', 'content-calendar'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le module Campaigns de Feedback Orchestrator est votre centre de pilotage pour toutes vos campagnes marketing. Creez des campagnes, associez-y des contenus, suivez les metriques et optimisez en temps reel.",
      },
      {
        type: 'methodology',
        title: "Comment l'utiliser ?",
        content:
          "1. **Creez une campagne** — Donnez-lui un nom, un objectif, une date de debut et de fin\n2. **Associez des contenus** — Liez les articles, posts et emails crees dans la Content Factory\n3. **Definissez vos KPIs cibles** — Nombre de leads, taux de conversion, ROI vise\n4. **Lancez et suivez** — Les metriques se mettent a jour au fil des resultats\n5. **Optimisez** — Ajustez les contenus et canaux en fonction des performances\n6. **Faites le bilan** — En fin de campagne, documentez les apprentissages",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Rendez-vous sur **Campaigns** pour creer votre premiere campagne. Chaque campagne est liee au **Marketing Dashboard** pour une vision en temps reel des resultats. Utilisez le **Content Calendar** pour coordonner la diffusion de vos contenus.",
      },
    ],
    linkedModule: {
      label: 'Campaigns',
      path: '/growth/campaigns',
      icon: 'Zap',
    },
  },
];
